/**
 * lib/pdfChrome.ts
 * ------------------------------------------------------------------
 * Shared LKC PDF chrome used by every analysis / mark-sheet PDF so
 * they all carry the same branded letterhead + footer without copy-
 * paste. Pairs with jspdf-autotable: draw the chrome in `didDrawPage`,
 * stamp the footers in a post-pass once the page count is known.
 *
 *   const geom = pageGeom('l');
 *   autoTable(pdf, {
 *     ...,
 *     margin: { top: CONTENT_TOP, left: geom.MX, right: geom.MX, bottom: 14 },
 *     startY: CONTENT_TOP,
 *     didDrawPage: () => drawLetterhead(pdf, { title, metaLine, logoDataUrl, geom }),
 *   });
 *   stampFooters(pdf, { geom, generatedBy });
 * ------------------------------------------------------------------
 */
import { jsPDF } from 'jspdf';
import { SCHOOL } from '@/lib/school.config';
import { BRAND, brandRgb } from '@/lib/brand.palette';

export type RGB = [number, number, number];

/**
 * House palette (crest navy + gold, plus neutral greys).
 *
 * Navy and gold are DERIVED from `lib/brand.palette`, not written out here. They
 * had been a third hand-kept copy of the crest colours, which is how a sister
 * deployment ended up printing every one of its PDFs in another school's navy —
 * the numbers were correct for whoever wrote them first and nobody re-read them.
 * The greys below are print furniture, not brand, so they stay literal.
 */
export const PDF = {
  NAVY: brandRgb(BRAND.primary),
  GOLD: brandRgb(BRAND.secondary),
  INK: [31, 41, 55] as RGB,
  MUTED: [107, 114, 128] as RGB,
  RULE: [209, 213, 219] as RGB,
  ZEBRA: [245, 247, 250] as RGB,
  BAND: [240, 244, 250] as RGB,
  READONLY_BG: [238, 240, 243] as RGB,
  WHITE: [255, 255, 255] as RGB,
};

/** Top margin reserved for the letterhead (table body starts here). */
export const CONTENT_TOP = 32;

export interface PageGeom {
  PW: number;
  PH: number;
  MX: number;
  right: number;
}

export function pageGeom(orientation: 'p' | 'l'): PageGeom {
  const PW = orientation === 'l' ? 297 : 210;
  const PH = orientation === 'l' ? 210 : 297;
  const MX = 12;
  return { PW, PH, MX, right: PW - MX };
}

export interface LetterheadOptions {
  /** Document title, e.g. 'MARK SHEET', 'STUDENT ANALYSIS'. */
  title: string;
  /** Right-aligned meta line under the title, e.g. 'Accounting · Form 3 · Term 1 2026'. */
  metaLine?: string;
  logoDataUrl?: string | null;
  geom: PageGeom;
}

/** Draw the navy letterhead + gold rule at the top of the current page. */
export function drawLetterhead(d: jsPDF, o: LetterheadOptions): void {
  const { geom } = o;
  let y = 12;
  let hasLogo = false;
  if (o.logoDataUrl) {
    try {
      d.addImage(o.logoDataUrl, 'PNG', geom.MX, y - 1, 16, 16);
      hasLogo = true;
    } catch {
      /* ignore broken logo */
    }
  }
  // No-logo (ink-saver) working documents: shift the wordmark to the margin
  // so there's no empty gap where the logo would be.
  const textX = hasLogo ? geom.MX + 20 : geom.MX;
  d.setTextColor(...PDF.NAVY);
  d.setFont('helvetica', 'bold');
  d.setFontSize(14);
  d.text(SCHOOL.name, textX, y + 4);
  d.setFont('helvetica', 'normal');
  d.setFontSize(8);
  d.setTextColor(...PDF.MUTED);
  d.text(
    `${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}  ·  ${SCHOOL.contact.website}`,
    textX,
    y + 9,
  );
  d.setTextColor(...PDF.NAVY);
  d.setFont('helvetica', 'bold');
  d.setFontSize(13);
  d.text(o.title, geom.right, y + 3, { align: 'right' });
  if (o.metaLine) {
    d.setFont('helvetica', 'normal');
    d.setFontSize(8.5);
    d.setTextColor(...PDF.MUTED);
    d.text(o.metaLine, geom.right, y + 8, { align: 'right' });
  }
  y += 18;
  d.setDrawColor(...PDF.GOLD);
  d.setLineWidth(0.8);
  d.line(geom.MX, y, geom.right, y);
}

export interface FooterOptions {
  geom: PageGeom;
  generatedBy?: string;
  /** The page geometry for one page, when a document mixes orientations
   *  (the markbook, from Al-Nur: AS/A2 sheets portrait, the rest landscape).
   *  Return undefined for a page to fall back to `geom`. */
  geomFor?: (page: number) => PageGeom | undefined;
}

/** Stamp the footer (motto · date · author, and "Page x of y") on every page. */
export function stampFooters(d: jsPDF, o: FooterOptions): void {
  const pages = d.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    const geom = o.geomFor?.(p) ?? o.geom;
    d.setPage(p);
    d.setDrawColor(...PDF.RULE);
    d.setLineWidth(0.2);
    d.line(geom.MX, geom.PH - 9, geom.right, geom.PH - 9);
    d.setTextColor(...PDF.MUTED);
    d.setFont('helvetica', 'normal');
    d.setFontSize(7);
    d.text(
      `${SCHOOL.motto}  ·  Generated ${new Date().toLocaleDateString()}${
        o.generatedBy ? `  ·  ${o.generatedBy}` : ''
      }`,
      geom.MX,
      geom.PH - 5,
    );
    d.text(`Page ${p} of ${pages}`, geom.right, geom.PH - 5, {
      align: 'right',
    });
  }
}
